// src/pages/PrivacyControls.tsx
import { Trans, useTranslation } from "react-i18next";
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeftIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { API_BASE_URL, apiPost, getAuthHeaders, join } from "../api/client";
import { companyRoute } from "../utils/routeHelpers";
import { formatAnyDateToLocale } from "../utils/dateUtils";
import { A } from "../styles/appleClasses";

/** Tolerant GET: prefers JSON; gracefully falls back to text/empty. */
async function apiGet<T>(url: string): Promise<T> {
  const r = await fetch(join(API_BASE_URL, url), {
    credentials: "include",
    headers: { ...getAuthHeaders(), Accept: "application/json" },
  });

  const bodyText = await r.text();

  if (!r.ok) {
    try {
      const errObj = JSON.parse(bodyText);
      throw new Error(errObj?.detail || JSON.stringify(errObj));
    } catch {
      throw new Error(bodyText || `HTTP ${r.status}`);
    }
  }

  const ct = r.headers.get("content-type") || "";
  if (ct.includes("application/json")) {
    try {
      return JSON.parse(bodyText);
    } catch {
      // fall through
    }
  }

  return bodyText as unknown as T;
}

/** Normalize any backend response into the shape this page expects. */
function normalizePrivacy(res: any): {
  consent_contribute: boolean;
  consent_contribute_at: string | null;
} {
  if (!res) return { consent_contribute: false, consent_contribute_at: null };

  if (typeof res === "object") {
    return {
      consent_contribute: !!(res as any).consent_contribute,
      consent_contribute_at:
        ((res as any).consent_contribute_at as string | undefined) || null,
    };
  }

  if (typeof res === "string") {
    const s = res.trim().toLowerCase();

    if (s === "true")
      return { consent_contribute: true, consent_contribute_at: null };
    if (s === "false" || s === "ok" || s === "") {
      return { consent_contribute: false, consent_contribute_at: null };
    }

    try {
      const parsed = JSON.parse(res);
      return normalizePrivacy(parsed);
    } catch {
      return { consent_contribute: false, consent_contribute_at: null };
    }
  }

  return { consent_contribute: false, consent_contribute_at: null };
}

function PrivacyPolicyLink() {
  const { t } = useTranslation();
  return (
    <a
      className="text-appleblue hover:underline"
      href="https://www.nimos.cloud/privacy"
      target="_blank"
      rel="noopener noreferrer"
    >
      {t("shell.footer.privacyPolicy")}
    </a>
  );
}

export default function PrivacyControls() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { company_id } = useParams<{ company_id?: string }>();

  const q = useMemo(
    () => (company_id ? `?company_id=${encodeURIComponent(company_id)}` : ""),
    [company_id],
  );

  const homeURL = companyRoute(company_id, "/");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [ts, setTs] = useState<string | null>(null);
  const [err, setErr] = useState<{ raw: string } | { fallback: "load" | "save" } | null>(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const raw = await apiGet<any>(`/api/company/privacy${q}`);
        if (cancelled) return;
        const res = normalizePrivacy(raw);
        setEnabled(res.consent_contribute);
        setTs(res.consent_contribute_at);
      } catch (e: any) {
        if (cancelled) return;
        const message = e?.message;
        setErr(message ? { raw: message } : { fallback: "load" });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [q]);

  async function toggle(next: boolean) {
    if (saving) return;
    setErr(null);
    setSaving(true);

    const prev = enabled;
    setEnabled(next); // optimistic

    try {
      const raw = await apiPost(`/api/company/privacy${q}`, {
        consent_contribute: next,
      });
      const res = normalizePrivacy(raw);
      setEnabled(res.consent_contribute);
      setTs(res.consent_contribute_at);
    } catch (e: any) {
      setEnabled(prev);
      const message = e?.message;
      setErr(message ? { raw: message } : { fallback: "save" });
    } finally {
      setSaving(false);
    }
  }

  const helpCls = A.label;

  // Header icon button (Apple Standard)
  const iconBtn = `${A.iconBtn} inline-flex items-center justify-center hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-appleblueD`;

  return (
    <div
      className={`min-h-screen bg-gray-50 ${A.body} flex flex-col items-center pt-4 sm:pt-6`}
    >
      {/* Header: arrow left, centered title, X */}
      <div className="sticky top-0 z-30 bg-gray-50 w-full max-w-2xl flex items-center border-b border-gray-100 px-2 pb-2 mb-2">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className={iconBtn}
          aria-label={t("directory.common.back")}
        >
          <ArrowLeftIcon className="w-6 h-6 text-gray-400 hover:text-gray-600 transition" />
        </button>

        <h1 className={`flex-1 text-center ${A.title}`}>{t("privacyControls.title")}</h1>

        <button
          type="button"
          onClick={() => navigate(homeURL)}
          className={iconBtn}
          aria-label={t("directory.common.close")}
        >
          <XMarkIcon className="w-6 h-6 text-gray-400 hover:text-gray-600 transition" />
        </button>
      </div>

      <div className="w-full max-w-2xl px-2 pb-10">
        <div className={`${A.card} p-5 sm:p-6`}>
          <div className={`${A.label} mb-4`}>
            {t("privacyControls.description")}
          </div>

          <div className="border border-gray-200 rounded-2xl p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="text-[13px] font-semibold text-gray-700">
                  {t("privacyControls.allowContribution")}
                </div>
                <div className={helpCls}>
                  {t("privacyControls.contributionHelp")}
                </div>

                {!loading && enabled && ts && (
                  <div className="mt-1 text-[11px] text-gray-400">
                    {t("privacyControls.enabledOn", { date: formatAnyDateToLocale(ts) })}
                  </div>
                )}
              </div>

              {/* Toggle */}
              <button
                type="button"
                disabled={loading || saving}
                onClick={() => toggle(!enabled)}
                className={[
                  "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition",
                  "focus:outline-none focus:ring-2 focus:ring-appleblueD",
                  loading || saving
                    ? "opacity-60 cursor-not-allowed"
                    : "cursor-pointer",
                  enabled ? "bg-appleblue" : "bg-gray-200",
                ].join(" ")}
                aria-pressed={enabled}
                aria-label={t("privacyControls.toggleContribution")}
              >
                <span
                  className={[
                    "inline-block h-5 w-5 transform rounded-full bg-white shadow transition",
                    enabled ? "translate-x-5" : "translate-x-1",
                  ].join(" ")}
                />
              </button>
            </div>

            {/* Status line */}
            <div className="mt-3" style={{ minHeight: 20 }}>
              {loading && <div className={helpCls}>{t("directory.common.loading")}</div>}
              {!loading && saving && <div className={helpCls}>{t("directory.common.savingEllipsis")}</div>}
              {err && <div className="text-[13px] text-red-600">{"raw" in err ? err.raw : err.fallback === "load" ? t("privacyControls.errors.load") : t("privacyControls.errors.save")}</div>}
            </div>
          </div>

          <div className={`mt-6 ${A.label}`}>
            <Trans
              t={t}
              i18nKey="privacyControls.policyNotice"
              components={{ policy: <PrivacyPolicyLink /> }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
